import { useState } from 'react';
import { Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import Card from '../../../components/ui/Card.jsx';
import Button from '../../../components/ui/Button.jsx';
import TemplatePicker, { templatePickerToPayload } from './TemplatePicker.jsx';
import { useApprovedTemplates, useSendTemplateBroadcast } from '../../../hooks/useWhatsApp.js';
import { getApiErrorMessage } from '../../../lib/api.js';

function TemplatesTab() {
  const { data: resp } = useApprovedTemplates();
  const templates = resp?.data?.data || [];
  const sendMutation = useSendTemplateBroadcast();

  const [pickerValue, setPickerValue] = useState({ selectedKey: null, params: [] });
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  const handleSend = async () => {
    const payload = templatePickerToPayload(pickerValue, templates);
    if (!payload) {
      setSendError('Please select a template first.');
      return;
    }
    if (!window.confirm(`Send template "${payload.template_name}" (${payload.language}) to EVERY WhatsApp-enabled employee?`)) {
      return;
    }

    setSendError(null);
    setSendResult(null);
    try {
      const resp = await sendMutation.mutateAsync({
        ...payload,
        target: { type: 'all' },
      });
      setSendResult(resp?.data || resp);
    } catch (e) {
      setSendError(getApiErrorMessage(e, 'Failed to send template'));
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="font-semibold mb-1">Send approved Meta template to everyone</p>
            <p>
              Pick a template, fill in any variables, and blast it to every WhatsApp-enabled
              employee. For targeted sends (specific users / company / designation), use the
              <b> Broadcasts</b> tab instead — it now supports template selection too.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <TemplatePicker value={pickerValue} onChange={setPickerValue} />

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={sendMutation.isPending || !pickerValue.selectedKey}
          >
            <Send className="mr-2 h-4 w-4" />
            {sendMutation.isPending ? 'Sending…' : 'Send to all employees'}
          </Button>
        </div>

        {sendError && (
          <div className="mt-4 p-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <div>
                <p className="font-semibold">Send failed</p>
                <p className="mt-1">{sendError}</p>
              </div>
            </div>
          </div>
        )}

        {sendResult?.success && (
          <div className="mt-4 p-3 rounded border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-green-600 dark:text-green-400" />
              <div className="text-gray-800 dark:text-gray-200">
                <p className="font-semibold text-green-700 dark:text-green-300">Broadcast complete</p>
                <p className="mt-1">
                  Campaign #{sendResult.campaign_id} &middot; sent: <b>{sendResult.stats?.sent ?? 0}</b>
                  {' '}&middot; failed: <b>{sendResult.stats?.failed ?? 0}</b>
                  {' '}(of {sendResult.stats?.total ?? 0} targeted)
                </p>
                {sendResult.stats?.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">
                      Show {sendResult.stats.errors.length} error{sendResult.stats.errors.length !== 1 ? 's' : ''}
                    </summary>
                    <ul className="mt-2 text-xs font-mono space-y-1 max-h-40 overflow-auto">
                      {sendResult.stats.errors.slice(0, 50).map((e, i) => (
                        <li key={i}>user {e.user_id}: {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default TemplatesTab;
